/**
 * The one TanStack Query client for the whole app (STEP 8 "Caching").
 * Defaults tuned for a desktop enterprise HMIS, not a public website:
 * data that changes rarely (master data, lookups) should stay cached
 * far longer than data a clinician is actively acting on (an open
 * Encounter). Per-query `staleTime` overrides live with the query
 * itself once real feature modules exist (Stage F1+) — these are
 * sane, conservative global defaults, not a ceiling.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@app-types/api.types';
import { isRetryableError } from './retry';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => failureCount < 2 && isRetryableError(error),
      retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 5_000),
    },
    mutations: {
      // Mutations never auto-retry by default — a retried POST could double-submit a
      // real clinical/financial action. Retry, if ever needed, is opt-in per mutation.
      retry: false,
      onError: (error) => {
        if (error instanceof ApiError && error.statusCode === 401) {
          // Session already cleared by the http-client's own interceptor by the time
          // this fires — nothing to do here beyond letting the UI's own route guard
          // redirect, which it will on the next render once auth state clears.
          return;
        }
      },
    },
  },
});
